'use client';

import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  cn,
  ghostAccentIconTileClass,
  previewPanelSurfaceClass,
  Skeleton,
} from '@up2stay/up2promo-ui';
import { ArrowRight, ChevronRight, Sparkles } from 'lucide-react';
import type { ReactNode } from 'react';

import { useRelativeTime } from '@/core/components/leads/lead-detail/format-relative-time';
import {
  REPUTATION_SECTION_LABEL_CLASS,
  REPUTATION_SECTION_MUTED_CLASS,
  REPUTATION_SECTION_TITLE_CLASS,
} from '@/core/components/reputation/reputation-surface';

/** Shared shape for reputation + competitor-board AI summaries. */
export type AiSummaryLike = {
  status: string;
  summary?: string;
  actionItems?: string[];
  generatedAt?: string;
};

export type AiSummaryCardLabels = {
  title: string;
  loading: string;
  unavailable: string;
  nextSteps: string;
  openReviews: string;
  updatedAgo: string;
};

/**
 * Shared AI / KI reputation summary card (dashboard + location Overview).
 * Status Board narrative first — charts live elsewhere.
 */
export function AiSummaryCard({
  summary,
  loading,
  unavailable,
  labels,
  locale,
  onInbox,
  ratingMeta,
  hideWhenUnavailable = true,
  openLabel,
  onOpen,
  hideFooter = false,
}: {
  summary: AiSummaryLike | undefined;
  loading: boolean;
  unavailable: boolean;
  labels: AiSummaryCardLabels;
  locale: string;
  onInbox?: () => void;
  /** Optional rating / volume line above the narrative. */
  ratingMeta?: ReactNode;
  /** Dashboard hides entirely when unavailable; location Overview keeps the strip. */
  hideWhenUnavailable?: boolean;
  openLabel?: string;
  onOpen?: () => void;
  /** Competitor board: narrative only, no inbox CTA. */
  hideFooter?: boolean;
}) {
  const relativeUpdated = useRelativeTime(
    summary?.generatedAt && summary.status === 'ready'
      ? summary.generatedAt
      : null,
    locale,
  );

  if (hideWhenUnavailable && unavailable && !summary?.summary) {
    return null;
  }

  const updatedLabel = relativeUpdated
    ? labels.updatedAgo.replace('{time}', relativeUpdated)
    : null;
  const openAction = onOpen ?? onInbox;
  const openText = openLabel ?? labels.openReviews;

  return (
    <Card
      className={cn(
        'min-w-0 w-full overflow-hidden shadow-none',
        previewPanelSurfaceClass,
      )}
    >
      <CardHeader className="gap-2 px-5 pb-2 pt-5 sm:px-6 sm:pt-6">
        <div className="flex items-center justify-between gap-2">
          <CardTitle
            className={cn(
              'flex items-center gap-2.5',
              REPUTATION_SECTION_TITLE_CLASS,
            )}
          >
            <span
              className={cn(
                'flex size-8 shrink-0 items-center justify-center rounded-[var(--r-control)]',
                ghostAccentIconTileClass,
              )}
              aria-hidden
            >
              <Sparkles className="size-4" />
            </span>
            {labels.title}
          </CardTitle>
          {updatedLabel ? (
            <span className="text-muted-foreground shrink-0 text-xs">
              {updatedLabel}
            </span>
          ) : null}
        </div>
      </CardHeader>
      <CardContent className="flex flex-col gap-4 px-5 pb-5 sm:px-6 sm:pb-6">
        {ratingMeta}

        {loading ? (
          <div className="flex flex-col gap-2">
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-5/6" />
            <p className={cn(REPUTATION_SECTION_MUTED_CLASS, 'text-xs')}>
              {labels.loading}
            </p>
          </div>
        ) : unavailable || !summary?.summary ? (
          <p className={cn(REPUTATION_SECTION_MUTED_CLASS, 'text-pretty')}>
            {labels.unavailable}
          </p>
        ) : (
          <>
            <p className="text-pretty text-sm leading-[1.65]">
              {summary.summary}
            </p>
            {(summary.actionItems?.length ?? 0) > 0 ? (
              <div className="flex flex-col gap-2">
                <p className={REPUTATION_SECTION_LABEL_CLASS}>
                  {labels.nextSteps}
                </p>
                <ol className="flex flex-col gap-2">
                  {(summary.actionItems ?? []).map((item, index) => (
                    <li key={item}>
                      <StepRow
                        index={index + 1}
                        text={item}
                        onClick={onInbox}
                      />
                    </li>
                  ))}
                </ol>
              </div>
            ) : null}
          </>
        )}

        {!hideFooter && openAction ? (
          <Button variant="ghostAccent" size="sm" onClick={openAction}>
            {openText}
            <ArrowRight className="ml-1 h-3.5 w-3.5" />
          </Button>
        ) : null}
      </CardContent>
    </Card>
  );
}

/** Numbered next-step row; interactive when `onClick` is set. */
function StepRow({
  index,
  text,
  onClick,
}: {
  index: number;
  text: string;
  onClick?: () => void;
}) {
  const content = (
    <>
      <span
        className="flex size-6 shrink-0 items-center justify-center rounded-full bg-[color:var(--ghost-accent-bg)] text-xs font-bold text-[color:var(--ghost-accent-fg)]"
        aria-hidden
      >
        {index}
      </span>
      <span className="min-w-0 flex-1 text-[13.5px] leading-snug">{text}</span>
      {onClick ? (
        <ChevronRight
          className="text-muted-foreground size-4 shrink-0"
          aria-hidden
        />
      ) : null}
    </>
  );
  const rowClass =
    'flex min-h-11 w-full items-center gap-3 rounded-[var(--radius-nested)] border border-border bg-card px-3.5 py-2.5 text-left';
  return onClick ? (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        rowClass,
        'hover:bg-muted/50 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
      )}
    >
      {content}
    </button>
  ) : (
    <div className={rowClass}>{content}</div>
  );
}
